import axios from 'axios/index';
import { stringify } from 'qs';
import API from '../constants/server';
import { stripe } from '../constants';
import { JOBS_LIST_PAGE_SIZE, PAYROLL_RUNS_LIST_PAGE_SIZE, USERS_LIST_PAGE_SIZE, limitSearchUsersResults,
  adminAppLanguageId } from '../constants/general';
import { yearMonthDayFormat } from '../constants/date';
import { getNowInTimeZone } from '../helpers/timezones';
import { httpClient, httpClientGrass, getSavedAuthToken } from './httpClient';
import UsersService from '../services/UsersService';

/*
*
* Users
*
* */

export const login = (email, password) =>
  axios.post(`${API}/users/login`, { email, password });

export const logout = () =>
  httpClient.delete(`/users/logout`);

export const loginWithToken = token =>
  httpClient.post(`/users/access-token-verify`, { token });

export const resetPassword = formData =>
  axios.post(`${API}/users/reset`, formData);

export const changePassword = data =>
  httpClient.post(`/users/change-user-password`, data);

export const createAdmin = userData =>
  httpClient.post(`/users/admin`, userData);

export const editAdmin = (id, data) =>
  httpClient.put(`/users/admin`, { id, data });

export const getAdmins = skip => {
  const queryString = `filter={"skip":${skip}}`;
  return httpClient.get(`/users/admin?${queryString}`);
};

export const getUser = id =>
  httpClient.get(`/users/${id}`);

export const searchUsers = searchWord => {
  const queryObj = {
    filter: {
      where: {
        or: [
          {
            first_name: {
              ilike: `%${searchWord}%`
            }
          },
          {
            last_name: {
              ilike: `%${searchWord}%`
            }
          },
          {
            email: {
              ilike: `%${searchWord}%`
            }
          }
        ]
      },
      limitSearchUsersResults
    }
  };
  const queryString = stringify(queryObj);
  return httpClient.get(`/users?${queryString}`);
};

export const searchEmployers = value => {
  const queryString = stringify({
    value
  });
  return httpClient.get(`/users/search-employers?${queryString}`);
};

// We used this endpoint for searching workers in bulk messaging, for searching lta workers and workers for direct messages as employer:
export const searchWorkers = (value, isLta = false) =>
  httpClient.get(`/users/search-workers`, { params: { value, filter_out_lta: isLta } });

export const searchWorkersForJob = (value, searchData) => {
  const { job_skill_id, ranking_number, job_id, job_date } = searchData;
  const queryString = stringify({
    value,
    job_skill_id,
    ranking_number,
    job_id,
    job_date
  });
  return httpClient.get(`/users/search-workers-for-job?${queryString}`);
};

/**
 * @param {string} value
 * @param {number} [company_id]
 */
export function searchFavorites (value, company_id) {
  const params = { value };

  if (typeof company_id === 'number') {
    params.company_id = company_id;
  }

  return httpClient.get(`/users/search-workers-for-fav`, { params });
}

/*
*
* References
*
* */
export const getReference = token =>
  axios.get(`${API}/user_references/income-reference?token=${token}`);

export const sendReference = formData =>
  axios.post(`${API}/user_references/complete-reference`, formData);

export const loadLanguages = () =>
  httpClient.get(`/languages`);

export const getUserReferences = user_id =>
  httpClient.get(`${API}/user_references/admin/find-user`, { params: { user_id } });

/*
 *
 * Interviews
 *
 */
export const getInterviews = () =>
  httpClient.get(`/orientation_dates/admin/interviews`);

export const getInterview = id =>
  httpClient.get(`/orientation_dates/find-user?user_id=${id}`);

export const updateInterview = data =>
  httpClient.put(`/orientation_dates/update-interview`, data);

export const bookSlot = data =>
  httpClient.post(`/orientation_dates/slot`, data);

export const getSlots = time_period =>
  httpClient.get(`/orientation_dates/show-slots?time_period=${time_period}`);

export const createNewInterview = interviewData =>
  httpClient.post(`/orientation_dates/slot`, interviewData);

/*
 *
 * Checkr
 *
 */
export const makeDecision = (compliance_id, decision) =>
  httpClient.post(`${API}/compliances/after-checkr-decision?compliance_id=${compliance_id}&decision=${decision}`, {});

export const getComplianceUpdatesById = compliance_id =>
  httpClient.get(`${API}/compliance_updates?filter={"where":{"compliance_id":"${compliance_id}"}}`);

/**
 *
 * Locations
 *
 * */
export const getLocationsList = () =>
  httpClient.get(`/job_location_references`);

/**
 * @param {number} id
 * */
export const getCompanyLocationsById = id => {
  const queryObj = {
    filter: {
      where: {
        company_id: id
      },
      order: 'location_name ASC',
      include: 'media'
    }
  };
  const queryString = stringify(queryObj);
  return httpClient.get(`/locations?${queryString}`);
};

export const getEmployerLocationsList = () =>
  httpClient.get(`/locations/list/employer`);

export const getCompanyInfoByEmployer = () =>
  httpClient.get(`/companies/employer`);

/**
 * @param {number} id
 * */
export const getCompanyDepartmentsById = id => {
  const queryObj = {
    filter: {
      where: {
        company_id: id
      }
    }
  };
  const queryString = stringify(queryObj);
  return httpClient.get(`/company_departments?${queryString}`);
};

/**
 * @param {number} id
 * @param {object} data
 * */
export const editCompanyDepartmentById = (id, data) =>
  httpClient.patch(`/company_departments/${id}`, data);

/**
 * @param {object} data
 * @param {number} data.company_id
 * @param {string} data.name
 * */
export const postCompanyDepartment = data =>
  httpClient.post(`/company_departments`, data);

/**
 * @param {string} name
 * */
export const getCompaniesByName = name =>
  httpClient.get(`/companies/search-by-name?name=${name}`);

export const deleteLocation = id =>
  httpClient.delete(`/locations/${id}`);

export const createLocation = data =>
  httpClient.post(`/locations`, data);

export const editLocation = (id, payload) =>
  httpClient.put(`/locations/${id}`, payload);

/**
 *
 * Dashboard
 * @param {string} startDate in format 'MM-DD-YYYY'
 * @param {number} days_to_take
 * @param {string} [role=admin]
 *
 * */
export const getDashboardJobs = (startDate, days_to_take, role = UsersService.getRoles().admin) =>
  httpClient.get(`/jobs/${role}/dashboard/${startDate}?days_to_take=${days_to_take}`);

/**
 *
 * Companies
 *
 * */

export const getCompanies = (skip = 0, filterData) => {
  const { queryString } = filterData;
  const queryParams = queryString || '';
  const query = stringify({
    skip,
    limit: USERS_LIST_PAGE_SIZE
  }) + queryParams;

  return httpClient.get(`/companies/admin/list?${query}`);
};

export const createCompany = payload =>
  httpClient.post(`/companies`, payload);

export const editCompany = (id, payload) =>
  httpClient.put(`/companies/${id}`, payload);

export const getCompanyDetails = id =>
  httpClient.get(`/companies/admin/${id}`);

export const addCustomRate = data =>
  httpClient.post(`/skill_level_geolocation_rates/admin/add-company-rate`, data);

export const deleteCustomRate = id =>
  httpClient.delete(`/skill_level_geolocation_rates/${id}`);

/**
 *
 * Employers
 *
 * */

export const getEmployers = (skip = 0, filterData) => {
  const { queryString } = filterData;
  const queryParams = queryString || '';
  const query = stringify({
    skip,
    limit: USERS_LIST_PAGE_SIZE
  }) + queryParams;

  return httpClient.get(`/users/admin/employers-list?${query}`);
};

/**
 * @param {number} company_id
 * */
export const getContactsByCompanyId = company_id => {
  const queryString = stringify({
    filter: {
      where: {
        company_id,
        is_active: true
      }
    }
  });

  return httpClient.get(`/users?${queryString}`);
};

export const createEmployer = payload =>
  httpClient.post(`/users/admin/employer`, payload);

export const editEmployer = (id, data) =>
  httpClient.put(`/users/admin/employer`, { id, data });

/**
 *
 * Workers
 *
 * */
export const getWorkers = (skip = 0, filterData) => {
  const { queryString } = filterData;
  const queryParams = queryString || '';
  const query = stringify({
    skip,
    limit: USERS_LIST_PAGE_SIZE
  }) + queryParams;
  return httpClient.get(`/users/admin/workers-list?${query}`);
};

/**
 *
 * Prospects
 *
 * */
export const getProspects = (skip = 0, filterData) => {
  const { queryString } = filterData;
  const queryParams = queryString || '';
  const query = stringify({
    skip,
    limit: USERS_LIST_PAGE_SIZE
  }) + queryParams;
  return httpClientGrass.get(`/admin/prospects?${query}`);
};

/**
 * @param {number} id
 */
export const getWorkerDetail = id => httpClient.get(`/users/worker-detail/${id}`);

export const createWorker = payload =>
  httpClient.post(`/users/admin/worker`, payload);

export const editWorker = (id, data) =>
  httpClient.put(`/users/admin/worker`, { id, data });

export const getWorkerJobs = (skip, filter) => {
  const { worker_id, period, queryString } = filter;
  const queryParams = queryString ? `&${queryString}` : '';

  const query = stringify({
    filter: {
      skip,
      limit: JOBS_LIST_PAGE_SIZE
    }
  }) + queryParams;
  return httpClient.get(`/jobs/admin/worker-list/${worker_id}/${period}?${query}`);
};

export const getWorkerJobsCompleted = (worker_id) => {
  return httpClient.get(`/jobs/admin/worker-list/${worker_id}/completed`);
};

/**
 *
 * Media
 *
 * */

export const upload = (form, object_id, isCompany) => {
  const data = new FormData(form);
  const to = isCompany ? 'company-upload' : 'user-upload';
  return axios.post(`${API}/media/${object_id}/${to}`, data, {
    headers: { 'Content-Type': 'multipart/form-data', Authorization: getSavedAuthToken() }
  });
};

/**
*
* Jobs
*
* */

/**
 * @param {number} skip
 * @param {object} filter
 * */
export const getJobs = (skip, filter) => {
  const { queryString, period } = filter;
  const userRole = UsersService.getUserRole();
  const queryParams = queryString ? `&${queryString}` : '';
  const query = stringify({
    filter: {
      skip,
      limit: JOBS_LIST_PAGE_SIZE
    }
  }) + queryParams;

  return httpClient.get(`/jobs/${userRole}/list/${period}?${query}`);
};

export const getJob = id =>
  httpClient.get(`/jobs/employer/detail/${id}`);

export const editJob = (id, jobData) =>
  httpClient.put(`/jobs/${id}`, jobData);

export const createJob = jobData =>
  httpClient.post(`/jobs`, jobData);

export const createMultiJob = jobData => {
  const userRole = UsersService.getUserRole();
  return httpClient.post(`/jobs/${userRole}/create-multi-day`, jobData);
};

/**
 *
 * @param {number[]} jobs
 * @param {boolean} post_to_board
 *
 * */
export const postToBoard = (jobs, post_to_board = true) =>
  httpClient.post(`/jobs/post-to-board`, { jobs: jobs, post_to_board });

export const cancelJob = (id, role = UsersService.getRoles().admin) =>
  httpClient.post(`/jobs/${id}/${role}/cancel-job`, {});

export const approveJob = (id, signatureUrl) =>
  httpClient.post(`/jobs/${id}/employer/approve`, { data: signatureUrl });

export const approveMultiDayJob = (eventId, signatureUrl) =>
  httpClient.post(`/jobs/${eventId}/employer/approve-multi-day`, { data: signatureUrl });

export const unApproveJob = jobId =>
  httpClient.post(`/jobs/${jobId}/admin/unapprove`);

export const getPayCostRate = data => {
  const query = stringify(data, { skipNulls: true });
  return httpClient.get(`/skill_levels/skills-with-rates?${query}`);
};

export const notifyJob = (id, role = UsersService.getRoles().admin) =>
  httpClient.post(`/jobs/${id}/admin/notify`, {});

/**
*
* Job workers
*
* */
export const editJobWorker = (workerId, jobWorkerData) =>
  httpClient.put(`/job_workers/${workerId}`, jobWorkerData);

export const addJobWorker = workerData =>
  httpClient.post(`/job_workers`, workerData);

/**
*
* Events
*
* */
export const getEventDetailsData = event_id =>
  httpClient.get(`/events/employer/detail/${event_id}`);

/**
 * @param {number} company_id
 * @param {string} time_zone_id
 * @param {string} [jobStartDate] // in 'YYYY-MM-DD' format
 * @return {AxiosPromise<any>}
 */
export const getEvents = (company_id, time_zone_id, jobStartDate) => {
  let queryObj = {
    filter: {
      where: {
        and: [
          {
            company_id
          },
          {
            end_date: {
              gte: getNowInTimeZone(time_zone_id).format(yearMonthDayFormat)
            }
          }
        ]
      }
    }
  };
  if (jobStartDate) {
    queryObj = {
      filter: {
        where: {
          and: [
            {
              company_id
            },
            {
              start_date: { lte: jobStartDate }
            },
            {
              end_date: { gte: jobStartDate }
            }
          ]
        }
      }
    };
  }
  const queryString = stringify(queryObj);
  return httpClient.get(`/events?${queryString}`);
};

/**
 * @param {number} company_id
 * */
export const getEventsByCompanyId = company_id => {
  const query = stringify({
    filter: {
      where: {
        company_id
      }
    }
  });

  return httpClient.get(`/events?${query}`);
};

/**
 * @param {number} id
 * */
export const getEventById = id =>
  httpClient.get(`/events/${id}`);

export const editEvent = (id, eventData) =>
  httpClient.put(`/events/${id}`, eventData);

export const createEvent = (eventData) =>
  httpClient.post(`/events`, eventData);

/**
*
* Skills
*
* */
export const getSkills = () =>
  httpClient.get(`/job_skills/all/1`);

export const getExistingSkillLevels = () =>
  httpClient.get(`/skill_levels/all/${adminAppLanguageId}`);

export const getWorkerSkills = workerId =>
  httpClient.get(`/user_job_categories/worker-skills/${workerId}`);

export const changeWorkerSkill = skillData =>
  httpClient.put(`/user_job_categories/set-job-category`, skillData);

export const deleteWorkerSkill = workerSkillId =>
  httpClient.delete(`/user_job_categories/${workerSkillId}`);

export const addWorkerSkill = (user_id, job_skill_id, skill_level_id) => {
  const skillData = {
    user_id,
    job_skill_id,
    skill_level_id
  };
  return httpClient.post(`/user_job_categories/set-job-category`, skillData);
};

/**
 *
 * Job Categories
 *
 * */
export const getJobCategories = () =>
  httpClient.get(`/job_categories/all/${adminAppLanguageId}`);

/**
*
* Dress code
*
* */
export const getAllDressCodes = () =>
  httpClient.get(`/dress_codes`);

/**
 *
 * Payroll
 *
 * */

export const getPayrollRuns = skip => {
  const queryString = stringify({
    filter: {
      skip,
      limit: PAYROLL_RUNS_LIST_PAGE_SIZE
    }
  });
  return httpClient.get(`/services/payroll-runs?${queryString}`);
};

export const runPayroll = (date) =>
  httpClient.get(`/services/run-payroll/${date}`);

/**
 *
 * Firebase notifications
 *
 * */

export function postFcmToken (formData) {
  return httpClient.post(`/fcm_tokens`, formData);
}

export function deleteFcmToken (token) {
  return httpClient.delete(`/fcm_tokens/${token}`);
}

/**
 *
 * Conversations
 *
 * */

export function getConversations (offset = 0) {
  return httpClient.get(`/conversations?offset=${offset}`);
}

export function getConversationById (id) {
  return httpClient.get(`/conversations/${id}`);
}

export function getConversationMessages (conversationId, offset = 0) {
  return httpClient.get(`/conversations/${conversationId}/conversation_messages?offset=${offset}`);
}

export function postConversationMessage (conversationId, formData) {
  return httpClient.post(`/conversations/${conversationId}/conversation_messages`, formData);
}

export function putConversationParticipant (conversationId, participantId, formData) {
  return httpClient.put(`/conversations/${conversationId}/conversation_participants/${participantId}`, formData);
}

export function getUnreadCount () {
  return httpClient.get('/conversations/unread_count');
}

export function postSupportConversation (userId) {
  return httpClient.post('/conversations/support', { userId });
}

/**
 *
 * @param {string} type
 * @param {array} users
 * @param {number} [job_id]
 * @return {AxiosPromise<any>}
 */
export function postConversation ({ type, users, job_id }) {
  return httpClient.post('/conversations', { type, users, job_id });
}

export function sendBulkMessage (formData) {
  return httpClient.post('/services/bulk-message', formData);
}

export function getAddressDetails (address) {
  return httpClient.get(`/services/google/address-details?address=${address}`);
}

/**
 *
 * @param {number} jobId
 * @param {object} formData
 * @param {number} formData.tip_amount
 * @param {number[]} formData.workers
 * @return {AxiosPromise<any>}
 */
export function addTipsToWorkers (jobId, formData) {
  return httpClient.post(`/jobs/${jobId}/employer/add-tips`, formData);
}

/**
 *
 * @param {number} jobId
 * @param {object} formData
 * @param {number} formData.rating
 * @param {string} formData.comments
 * @param {boolean} formData.rehire
 * @param {number[]} formData.workers
 * @return {AxiosPromise<any>}
 */
export function employerRateWorkers (jobId, formData) {
  return httpClient.post(`/jobs/${jobId}/employer/rate-workers`, formData);
}

export const acceptTerms = () =>
  httpClient.post(`/users/accept-terms`);

export const acceptCSA = () =>
  httpClient.post(`/users/accept-client-service-agreement`);

/**
 *
 * Company payment
 *
 * */

export const getCompanyPayment = company_id => {
  return httpClient.get(`/company_payments/admin/get-payments`, { params: { company_id } });
};

export const getCompanyPaymentByEmployer = () =>
  httpClient.get(`/company_payments/employer`);

export const deleteCompanyPayment = company_id =>
  httpClient.delete(`/company_payments/${company_id}`);

/**
 *
 * Company LTA workers
 *
 * */

export const getLTAWorkers = (company_id, job_skill_id) => {
  const userRole = UsersService.getUserRole();
  return httpClient.get(`/company_lta_workers/${userRole}`, { params: { company_id, job_skill_id } });
};

export const addLTAWorker = (user_id, company_id) => {
  const userRole = UsersService.getUserRole();
  return httpClient.post(`/company_lta_workers/${userRole}`, { user_id, company_id });
};

export const removeLTAWorker = worker_id => {
  return httpClient.delete(`/company_lta_workers/admin/${worker_id}`);
};

/**
 *
 * Company favorites and blocks
 *
 * */

export const getFavAndBlocks = (payload = {}) => {
  const { company_id, status, skill, job_id, job_date, limit, offset } = payload;
  const userRole = UsersService.getUserRole();
  return httpClient.get(`/favorite_blocked_workers/${userRole}`, { params: { company_id, status, skill, job_date, job_id, limit, offset } });
};

/**
 * @param {object} data
 */
export function createFavBlockWorker (data) {
  return httpClient.post(`/favorite_blocked_workers/${UsersService.getUserRole()}`, data);
}

/**
 * @param {number} id
 * @param {object} data
 */
export function updateFavBlockWorker (id, data) {
  return httpClient.put(`/favorite_blocked_workers/${UsersService.getUserRole()}/${id}`, data);
}

/**
 * @param {number} id
 */
export function deleteFavBlockWorker (id) {
  return httpClient.delete(`/favorite_blocked_workers/${UsersService.getUserRole()}/${id}`);
}

/**
 *
 * Get stripe public key from API
 *
 * */
export const getStripePublicKey = () => httpClient.get(`/settings/stripe-key`);

/**
 *
 * Create stripe credit card record
 * @param {object} formData
 * @param {string} stripePublicKey
 * @see https://stripe.com/docs/testing#cards for testing
 *
 * */
export const createStripeCreditCard = (formData, stripePublicKey) =>
  axios.post(`${stripe}/tokens`, formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': `Bearer ${stripePublicKey}`
    }
  });

/**
 *
 * Create user stripe on Gravy
 * @param {number} company_id
 * @param {string} cc_token
 *
 * */
export const createStripeCustomer = (company_id, cc_token) => {
  const userRole = UsersService.getUserRole();
  const payload = { cc_token };
  if (UsersService.isAdmin()) {
    payload.company_id = company_id;
  }
  return httpClient.post(`/company_payments/${userRole}/create-payment`, payload);
};

/**
 *
 * Orientation locations
 *
 * */
export const getOrientationLocations = () =>
  httpClientGrass.get('/admin/orientation-locations');

export const putOrientationLocation = (id, formData) =>
  httpClientGrass.put(`/admin/orientation-locations/${id}`, formData);

/**
 * @param {object} formData
 * @param {string} formData.address
 * @param {string} formData.name
 * @param {number} formData.max_seats
 */
export const postOrientationLocation = formData =>
  httpClientGrass.post(`/admin/orientation-locations`, formData);

/**
 *
 * Orientations
 *
 * */
export const getOrientations = from_date =>
  httpClientGrass.get('/admin/orientations/by-date', { params: { from_date } });

/**
 * @param {object} formData
 * @param {number} formData.orientation_location_id
 * @param {string} formData.date
 * @param {string} formData.start_time
 * @param {string} formData.end_time
 */
export const postOrientation = formData =>
  httpClientGrass.post(`/admin/orientations`, formData);

export const getOrientDetails = id =>
  httpClientGrass.get(`/admin/orientations/${id}`);

/**
 * @param {number} orientation_id
 * @param {array} user_ids
 */
export const addOrientAttendees = (orientation_id, user_ids) =>
  httpClientGrass.post(`/admin/orientations/${orientation_id}/attendees`, { user_ids });

/**
 * @param {number} orientation_id
 * @param {number} user_id
 */
export const deleteOrientAttendee = (orientation_id, user_id) =>
  httpClientGrass.delete(`/admin/orientations/${orientation_id}/attendees`, { params: { user_id } });

/**
 *
 * Workers
 *
 * */
export const searchWorkersByStatus = (query, onboarding_status) =>
  httpClientGrass.get('/admin/workers', { params: { query, onboarding_status } });

/**
 *
 * Prospects
 *
 * */
export const approveProspect = (formData) =>
  httpClientGrass.put(`/admin/prospects/${formData.id}/approve`, formData);

export const checkProspect = (formData) =>
  httpClientGrass.put(`/admin/prospects/${formData.id}/check`, formData);

export const denyProspect = (formData) =>
  httpClientGrass.put(`/admin/prospects/${formData.id}/deny`, formData);

export const callbackProspect = (formData) =>
  httpClientGrass.put(`/admin/prospects/${formData.id}/callback`, formData);

export const convertProspect = (formData) =>
  httpClientGrass.put(`/admin/prospects/${formData.id}/convert`, formData);

export const updateProspectOrientation = (id, orientation_id) =>
  httpClientGrass.put(`/admin/prospects/${id}/orientation`, { orientation_id })

/**
 *
 * Assessments
 *
 * */
export const getAssessments = () =>
  httpClientGrass.get('/admin/assessments');

export const putAssessment = (id, formData) =>
  httpClientGrass.put(`/admin/assessments/${id}`, formData);

/**
 * @param {object} formData
 * @param {string} formData.name
 * @param {string} formData.en_typeform_url
 * @param {string} formData.es_typeform_url
 * @param {array} [formData.skill_ids]
 */
export const postAssessment = formData =>
  httpClientGrass.post(`/admin/assessments`, formData);



// WEBPACK FOOTER //
// ./src/api/index.js